<!--
AppButton Component — wraps PrimeVue Button for all action buttons.

Use for any clickable action (submit, cancel, navigation, toolbar, icon-only).
Forwards every PrimeVue Button prop and listener via $attrs and exposes the
default slot for custom content next to the label.

Self-explaining (skill `self-explaining-ui`):
  - `explanation` — the button's Explanation, shown by v-explain on hover,
    focus and (when disabled) tap. REQUIRED with `disabled` in src/domains/**
    (qa rule `disabled-needs-explanation`).
  - `disabled` + `explanation` → the button is rendered `aria-disabled="true"`
    instead of `disabled`: a real disabled button gets no mouse event and no
    focus, so the reason could never open. The click is swallowed (no @click
    runs) and theme.css keeps the disabled look.
  - `no-explanation="<reason>"` — the declared decision NOT to explain (level B
    only); rendered as `data-no-explanation`, harmless in the DOM.
-->

<template>
  <PrimeButton
    v-explain="explanation"
    v-bind="$attrs"
    :disabled="disabled && !explanation"
    :aria-disabled="disabled && explanation ? 'true' : undefined"
    :data-no-explanation="noExplanation || undefined"
  >
    <slot />
  </PrimeButton>
</template>

<script setup lang="ts">
import PrimeButton from 'primevue/button';

import type { ExplanationInput } from '../explanations/types';
import { vExplain } from '../explanations/vExplain';

defineOptions({ name: 'AppButton', inheritAttrs: false });

withDefaults(defineProps<{
  explanation?: ExplanationInput;
  disabled?: boolean;
  noExplanation?: string;
}>(), {
  explanation: null,
  disabled: false,
  noExplanation: '',
});
</script>
